import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "./ui/dialog";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "./ui/scroll-area";
import { FileText } from "lucide-react";

const PrivacyPolicy = () => {
    const { t } = useTranslation("settings");
    return (
        <>
            <Dialog>
                <DialogTrigger asChild>
                    <span className="underline cursor-pointer hover:text-[--accent-primary] transition-colors text-[--accent-primary]">Privacy Policy</span>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
                    <DialogHeader className="border-b border-border/50 pb-4 flex flex-row items-center gap-4">
                        <div className="flex items-center justify-center p-2 rounded-lg bg-[--accent-primary]/15">
                            <FileText className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <DialogTitle className="text-xl font-bold">Privacy Policy</DialogTitle>
                            <p className="text-xs text-muted-foreground mt-0.5">Last updated: July 2026</p>
                        </div>
                    </DialogHeader>

                    <ScrollArea className="h-[50vh] pr-4 my-4">
                        <div className="space-y-6 text-sm leading-relaxed text-slate-300">
                            
                            <div className="bg-[#0c1938]/30 border border-[#1d3570]/30 rounded-xl p-4 text-slate-300">
                                <p>
                                    This privacy notice for Bytemine Technologies Limited (doing business as Level2) (&quot;Level2,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), describes how and why we might collect, store, use, and/or share (&quot;process&quot;) your information when you use our services (&quot;Services&quot;).
                                </p>
                            </div>

                            {/* Section 1 */}
                            <div className="space-y-3">
                                <h3 className="text-base font-semibold text-white flex items-center gap-2 border-l-2 border-[--accent-primary] pl-3">
                                    1. Personal Information We Process
                                </h3>
                                <div className="space-y-2 text-slate-400 pl-4">
                                    <p>
                                        When you visit, use, or navigate our Services, we may process personal information depending on how you interact with Level2 and our Services, the choices you make, and the products and features you use. We collect personal information that you voluntarily provide to us when you register on the Services.
                                    </p>
                                    <p>
                                        <strong>Information Provided by You:</strong> All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.
                                    </p>
                                    <p>
                                        <strong>Automatically Collected Information:</strong> We automatically collect certain information when you visit, use, or navigate the Services (e.g. IP address, browser/device characteristics, operating system, and usage patterns) to maintain safety, security, and internal analytics.
                                    </p>
                                </div>
                            </div>

                            {/* Section 2 */}
                            <div className="space-y-3">
                                <h3 className="text-base font-semibold text-white flex items-center gap-2 border-l-2 border-[--accent-primary] pl-3">
                                    2. Sensitive Information &amp; Third Parties
                                </h3>
                                <div className="space-y-2 text-slate-400 pl-4">
                                    <p>
                                        <strong>Sensitive Personal Information:</strong> We do not process sensitive personal information.
                                    </p>
                                    <p>
                                        <strong>Third Party Data:</strong> We do not receive any information from third parties.
                                    </p>
                                </div>
                            </div>

                            {/* Section 3 */}
                            <div className="space-y-3">
                                <h3 className="text-base font-semibold text-white flex items-center gap-2 border-l-2 border-[--accent-primary] pl-3">
                                    3. Data Processing &amp; Sharing
                                </h3>
                                <div className="space-y-2 text-slate-400 pl-4">
                                    <p>
                                        We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We process your information only when we have a valid legal reason to do so.
                                    </p>
                                    <p>
                                        We may share information in specific situations and with specific third parties as detailed in our disclosures.
                                    </p>
                                </div>
                            </div>

                            {/* Section 4 */}
                            <div className="space-y-3">
                                <h3 className="text-base font-semibold text-white flex items-center gap-2 border-l-2 border-[--accent-primary] pl-3">
                                    4. Data Retention &amp; Safety
                                </h3>
                                <div className="space-y-2 text-slate-400 pl-4">
                                    <p>
                                        We keep your information for as long as necessary to fulfill the purposes outlined in this privacy notice unless a longer retention period is required or permitted by law.
                                    </p>
                                    <p>
                                        We have organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission or storage technology can be guaranteed to be 100% secure.
                                    </p>
                                </div>
                            </div>

                            {/* Section 5 */}
                            <div className="space-y-3">
                                <h3 className="text-base font-semibold text-white flex items-center gap-2 border-l-2 border-[--accent-primary] pl-3">
                                    5. Your Rights &amp; Contact Information
                                </h3>
                                <div className="space-y-2 text-slate-400 pl-4">
                                    <p>
                                        Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information.
                                    </p>
                                    <p>
                                        The easiest way to exercise your rights is by contacting us at <a href="mailto:contact@trylevel2.com" className="text-[--accent-primary] underline">contact@trylevel2.com</a>. We will consider and act upon any request in accordance with applicable data protection laws.
                                    </p>
                                </div>
                            </div>

                        </div>
                    </ScrollArea>

                    <DialogFooter className="border-t border-border/50 pt-4 flex justify-end">
                        <DialogClose asChild>
                            <Button type="button" className="bg-[--accent-primary] text-white hover:bg-[--accent-primary]/90 px-6 py-2 rounded-md transition-all duration-300">
                                Accept &amp; Close
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
export default PrivacyPolicy;